import { takeLatest, call, put, select } from "redux-saga/effects";
import { Creators as LobbyCreators } from "store/lobby/lobby-duck";
import { DrawerStatusEnum } from "typings/global.types";
import { isUnknown, removeUnnecessaryDocument } from "utils/functions";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import * as Api from "./attendance-api";
import { Types, Creators } from "./attendance-duck";
import { IItems, IItemsCheckin, IItemsCheckinDataTable, IRegisterData, ISummaryData } from "./attendance-model";

export const getFilterFromStateUserSession = (state) => state.UserSession;
export const getFilterFromState = (state) => state.Attendance;
export const getFilterFromLobbyState = (state) => state.Lobby;

export const onExtractRegisterData = (registersData: IRegisterData): IItems[] =>
  registersData.items.map((item) => {
    return {
      id: item.id,
      carrier: item.carrier,
      vehiclePlate: item.vehiclePlate,
      trailersplate: item.trailersPlates?.map((itemTrailersPlates) => itemTrailersPlates),
      processId: item.processId,
      process: item.process,
      eventTime: item.eventTime,
      gateEventId: item.gateEventId,
      vehicleEntryAuthorizationStatus: item.vehicleEntryAuthorizationStatus,
      saleChannelDescription: item.saleChannelDescription,
      saleChannelId: item.saleChannelId,
      hasWarning: item.hasWarning,
      local: item.eventType,
      documents: removeUnnecessaryDocument(item.documents, "Chave NF"),
      vehicleStepStatus: item.vehicleStepStatus,
      lenghtOfStay: item.lenghtOfStay,
      serviceStartTime: item.serviceStartTime,
      serviceFinishTime: item.serviceFinishTime,
      hasActiveTag: item.hasActiveTag,
    };
  });

export const onExtractSummaryData = (summary: ISummaryData): ISummaryData => {
  return {
    presented: summary.presented,
    averageTimePresented: summary.averageTimePresented === "0.00:00:00" ? null : summary.averageTimePresented.slice(0, -3),
    released: summary.released,
    notReleased: summary.notReleased,
    actives: summary.actives,
    finishOperation: summary.finishOperation,
    inOperation: summary.inOperation,
    waitingInOperation: summary.waitingInOperation,
    averageTimeActives: summary.averageTimeActives === "0.00:00:00" ? null : summary.averageTimeActives.slice(0, -3),
    warningFinishOperation: summary.warningFinishOperation,
    inRadiusWithCheckin: summary.inRadiusWithCheckin,
    inRadiusWithoutCheckin: summary.inRadiusWithoutCheckin,
  };
};

export const onExtractItemsCheckin = (items: IItemsCheckin[]): IItemsCheckinDataTable[] => {
  const itemsChekin = items?.map((item) => ({
    documentNumber: item.documentNumber,
    orderItems: item.orderItems?.map((orderItem) => ({
      orderNumber: orderItem.orderNumber,
      code: orderItem.code,
      description: orderItem.description,
      quantity: `${orderItem.quantity} ${orderItem.unitOfMeasurement ? orderItem.unitOfMeasurement : ""}`,
    })),
  }));
  return itemsChekin;
};

export function* onAttendanceFetchAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (isUnknown(userSessionState.userData.role)) {
    return;
  }
  try {
    yield put(Creators.attendanceShowLoadingTable());
    const attendance = yield select(getFilterFromState);
    const resp = yield call(Api.getAttendance, {
      locationId: userSessionState.userData.locationId,
      _size: attendance.filter.itemsByPage,
      _page: attendance.filter.pageIndex,
      orderbySchedule: attendance.filter.orderBy,
      filter: attendance.searchFilter,
      processIds: attendance.filterOptions.processId,
      saleChannelIds: attendance.filterOptions.saleChannelId,
      vehicleStepStatus: attendance.filterOptions.status,
    });
    const registersData = resp.data;

    const data = onExtractRegisterData(registersData);
    yield put(Creators.attendanceFetchRegister(data, registersData.totalItems));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("could_not_load_attendance");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.attendanceHideLoadingTable());
  }
}

export function* onSaveVehicleActionAsync(action: any) {
  try {
    yield put(Creators.attendanceShowLoading());
    yield put(Creators.attendanceDisabledButtonSaveVehicle());
    if (action.body.dock) {
      yield call(Api.saveRequestVehicleForDock, { ...action.body });
    } else {
      yield call(Api.saveRequestVehicleForGate, action.body.id);
    }
    yield put(Creators.attendanceHideModalVehicleAction());
    yield put(Creators.attendanceEnabledButtonSaveVehicle());
    const registrationSuccessfullyMessage = yield translate("vehicle_action_success_notification");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield onAttendanceFetchAsync();
  } catch (error) {
    console.error(error);
    yield put(Creators.attendanceEnabledButtonSaveVehicle());
    const registrationErrorMessage = yield getError(error) ?? translate("vehicle_action_error_notification");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.attendanceHideLoading());
  }
}

export function* onSaveReleaseVehicleActionAsync(action: any) {
  try {
    yield put(Creators.attendanceShowLoading());
    yield put(Creators.attendanceDisabledButtonReleaseVehicle());
    yield call(Api.saveReleaseVehicle, action.body.id);
    yield put(Creators.attendanceHideModalReleaseVehicleAction());
    yield put(Creators.attendanceEnabledButtonReleaseVehicle());
    const registrationSuccessfullyMessage = yield translate("release_vehicle_action_success_notification");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield onAttendanceFetchAsync();
  } catch (error) {
    console.error(error);
    yield put(Creators.attendanceEnabledButtonReleaseVehicle());
    const registrationErrorMessage = yield getError(error) ?? translate("release_vehicle_action_error_notification");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.attendanceHideLoading());
  }
}

export function* onSaveCheckinRegisterAsync(action: any) {
  try {
    const attendanceState = yield select(getFilterFromState);
    yield put(Creators.attendanceShowLoading());
    yield put(Creators.attendanceDisabledRegisterButton());
    yield call(Api.saveCheckinRegister, action.body);
    yield put(Creators.attendanceEnabledRegisterButton());
    yield put(Creators.attendanceHideDrawerCheckinRegister());
    const totalItemsCheckinPage = attendanceState.items;
    if (totalItemsCheckinPage.length === 1 && attendanceState.totalItems > 10) {
      const pageIndexValue = attendanceState.filter.pageIndex - 1;
      yield put(Creators.attendanceChangeFieldValue("pageIndex", pageIndexValue));
    }
    const registrationSuccessfullyMessage = yield translate("registration_successfully!");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield put(LobbyCreators.lobbySaleChannelActive(null));
    yield onAttendanceFetchAsync();
  } catch (error) {
    console.error(error);
    yield put(Creators.attendanceEnabledRegisterButton());
    const registrationErrorMessage = yield getError(error) ?? translate("cannot_register");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.attendanceHideLoading());
  }
}

export function* onUpdateCheckinAsync(action: any) {
  try {
    yield put(Creators.attendanceShowLoading());
    yield call(Api.updateChekin, action.body);
    yield put(Creators.attendanceHideDrawerViewCheckinRegister());
    const registrationSuccessfullyMessage = yield translate("update_successfully");
    yield onAttendanceFetchAsync();
    yield put(Creators.attendanceUpdateDrawerViewMode(DrawerStatusEnum.View));
    yield put(LobbyCreators.lobbyCheckinClearDrivers());
    yield put(LobbyCreators.lobbySaleChannelActive(null));
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
  } catch (error) {
    const registrationErrorMessage = yield getError(error) ?? translate("cannot_update_checkin");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.attendanceHideLoading());
  }
}

export function* onRemoveCheckinRegisterAsync(action: any) {
  try {
    const attendanceState = yield select(getFilterFromState);
    yield put(Creators.attendanceShowLoading());
    yield put(Creators.attendanceDisableExcludeButton());
    yield call(Api.deleteCheckinRegister, action.body.yardRegisterId);
    yield put(Creators.attendanceHideDrawerViewCheckinRegister());
    const totalItemsCheckinPage = attendanceState.items;
    if (totalItemsCheckinPage.length === 1 && attendanceState.totalItems > 10) {
      const pageIndexValue = attendanceState.filter.pageIndex - 1;
      yield put(Creators.attendanceChangeFieldValue("pageIndex", pageIndexValue));
    }
    const registrationSuccessfullyMessage = yield translate("successfully_removed");

    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield put(LobbyCreators.lobbySaleChannelActive(null));
    yield onAttendanceFetchAsync();
  } catch (error) {
    console.error(error);
    const registrationErrorMessage = yield getError(error) ?? translate("cannot_exclude");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.attendanceHideLoading());
    yield put(Creators.attendanceEnableExcludeButton());
  }
}

export function* onFetchItemVehicleEntryAuthorizationAsync(action: any) {
  try {
    yield put(LobbyCreators.lobbyDrawerShowLoading());
    const resp = yield call(Api.getEventsHistory, action.body.id);
    yield put(Creators.attendanceUpdateVehicleEntryAuthorizationRegisterActive(resp.data));
  } catch (err) {
    yield put(Creators.attendanceHideDrawerVehicleEntryAuthorization());
    console.error(err);
    const registrationErrorMessage = yield getError(err) ?? translate("could_not_load_events_history");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(LobbyCreators.lobbyDrawerHideLoading());
  }
}

export function* onFetchSummaryAsync() {
  try {
    yield put(Creators.attendanceShowLoadingSummary());
    const userSessionState = yield select(getFilterFromStateUserSession);
    const attendance = yield select(getFilterFromState);
    const resp = yield call(Api.getSummary, {
      locationId: userSessionState.userData.locationId,
      processIds: attendance.filterOptions.processId,
      saleChannelIds: attendance.filterOptions.saleChannelId,
      vehicleStepStatus: attendance.filterOptions.status,
    });
    const data = onExtractSummaryData(resp.data);
    yield put(Creators.attendanceUpdateActiveSummary(data));
  } catch (err) {
    console.error(err);
    const registrationErrorMessage = yield getError(err) ?? translate("could_not_load_summary");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.attendanceHideLoadingSummary());
  }
}

export const convertToVehicleStepDescription = (id, translation) => {
  switch (id) {
    case 0:
      return translation.ALL;
    case 1:
      return translation.PRESENTED_VEHICLE;
    case 2:
      return translation.NOT_RELEASED;
    case 3:
      return translation.RELEASED;
    case 4:
      return translation.ACTIVE;
    case 5:
      return translation.INTERN_QUEUE;
    case 6:
      return translation.IN_OPERATION;
    case 7:
      return translation.FINISH_OPERATION;
    case 8:
      return translation.IN_RADIUS;
    default:
      return null;
  }
};

export function* onFetchVehicleStatusAsync() {
  try {
    const vehicleStatusResponse = yield call(Api.getVehicleStatus);
    const translation = {
      ALL: yield translate("all"),
      PRESENTED_VEHICLE: yield translate("presented_vehicle"),
      NOT_RELEASED: yield translate("not_released"),
      RELEASED: yield translate("released"),
      ACTIVE: yield translate("vehicles_in_location"),
      INTERN_QUEUE: yield translate("intern_queue"),
      IN_OPERATION: yield translate("in_operation"),
      FINISH_OPERATION: yield translate("finished_operation"),
      IN_RADIUS: yield translate("in_radius"),
    };
    const vehicleStatusList = yield vehicleStatusResponse.data.map((x) => {
      return {
        id: x.id,
        description: convertToVehicleStepDescription(x.id, translation),
      };
    });
    const vehicleStatus = { vehicleStatusList };
    yield put(Creators.attendanceVehicleStatusList(vehicleStatus));
  } catch (error) {
    const requestErrorMessage = yield translate("could_not_load_vehicle_status");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onUpdateInboundAsync(action: any) {
  try {
    yield put(Creators.attendanceShowLoading());
    const lobbyState = yield select(getFilterFromLobbyState);
    yield call(Api.updateInbound, action.body, lobbyState.registerActive.id);
    yield put(Creators.attendanceHideDrawerViewRegister());
    const registrationSuccessfullyMessage = yield translate("update_successfully");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield put(Creators.attendanceUpdateDrawerViewMode(DrawerStatusEnum.View));
    yield put(LobbyCreators.lobbyCheckinClearDrivers());
    yield put(LobbyCreators.lobbySaleChannelActive(null));
    yield onAttendanceFetchAsync();
  } catch (error) {
    const registrationErrorMessage = yield getError(error) ?? translate("cannot_register");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.attendanceHideLoading());
  }
}

export function* onFetchItemsCheckinAsync(action: any) {
  try {
    yield put(Creators.attendanceShowLoading());
    const resp = yield call(Api.getItemsCheckin, action.body);
    const itemsCheckin = yield onExtractItemsCheckin(resp.data);
    yield put(Creators.attendanceUpdateItemsCheckin(itemsCheckin));
  } catch (err) {
    console.error(err);
    const fetchItemsErrorMessage = yield getError(err) ?? translate("could_not_load_items_checkin");
    yield YmsNotification({
      type: "error",
      description: fetchItemsErrorMessage,
    });
  } finally {
    yield put(Creators.attendanceHideLoading());
  }
}

export default function* AttendanceSagas() {
  yield takeLatest(Types.ATTENDANCE_FETCH_REGISTER_ASYNC, onAttendanceFetchAsync);
  yield takeLatest(Types.ATTENDANCE_SAVE_VEHICLE_ACTION_ASYNC, onSaveVehicleActionAsync);
  yield takeLatest(Types.ATTENDANCE_SAVE_RELEASE_VEHICLE_ACTION_ASYNC, onSaveReleaseVehicleActionAsync);
  yield takeLatest(Types.ATTENDANCE_SAVE_CHECKIN_REGISTER_ASYNC, onSaveCheckinRegisterAsync);
  yield takeLatest(Types.ATTENDANCE_REMOVE_CHECKIN_REGISTER_ASYNC, onRemoveCheckinRegisterAsync);
  yield takeLatest(Types.ATTENDANCE_UPDATE_CHECKIN_ASYNC, onUpdateCheckinAsync);
  yield takeLatest(Types.ATTENDANCE_FETCH_ITEM_VEHICLE_ENTRY_AUTHORIZATION_ASYNC, onFetchItemVehicleEntryAuthorizationAsync);
  yield takeLatest(Types.ATTENDANCE_FETCH_SUMMARY_ASYNC, onFetchSummaryAsync);
  yield takeLatest(Types.ATTENDANCE_FETCH_VEHICLE_STATUS_ASYNC, onFetchVehicleStatusAsync);
  yield takeLatest(Types.ATTENDANCE_UPDATE_INBOUND_ASYNC, onUpdateInboundAsync);
  yield takeLatest(Types.ATTENDANCE_FETCH_ITEMS_CHECKIN_ASYNC, onFetchItemsCheckinAsync);
}
